import React, { useState, useEffect, useRef } from 'react';
import { track } from '@vercel/analytics';
import { useLanguage } from '../i18n/useLanguage.jsx';


export default function StoryBox({ onContinue }) {
	const { t } = useLanguage();
	
	// Create story text as HTML string for the typewriter effect
	const getStoryHTML = () => {
		return `<span class="storybox-highlight">${t('storyBox.story1')}</span><br/><br/>${t('storyBox.story2')}<br/><br/>${t('storyBox.story3')}<br/><br/>${t('storyBox.story4')}<br/><br/><span class="storybox-highlight">${t('storyBox.story5')}</span>`;
	};

	// Get just the text content for counting visible characters
	const getTextContent = () => {
		return `${t('storyBox.story1')} ${t('storyBox.story2')} ${t('storyBox.story3')} ${t('storyBox.story4')} ${t('storyBox.story5')}`;
	};

	// We'll animate by revealing one character at a time, but only count visible text
	const [visibleChars, setVisibleChars] = useState(0);
	const [hasCompleted, setHasCompleted] = useState(false);
	const fullTextRef = useRef('');
	const textContentRef = useRef('');
	const frameCountRef = useRef(0);
	
	// Build the full text for typewriter effect
	useEffect(() => {
		const newHTML = getStoryHTML();
		const newTextContent = getTextContent();
		fullTextRef.current = newHTML;
		textContentRef.current = newTextContent;
		
		// Only reset if typewriter hasn't completed yet
		if (!hasCompleted) {
			setVisibleChars(0);
			frameCountRef.current = 0;
		} else {
			// If already completed, show full text immediately
			setVisibleChars(newTextContent.length);
		}
	}, [t, hasCompleted]);

	// Animate the typewriter effect using requestAnimationFrame for consistent timing
	useEffect(() => {
		if (!textContentRef.current.length) return;
		if (visibleChars >= textContentRef.current.length) {
			setHasCompleted(true);
			return;
		}
		
		let animationFrame;
		const animate = () => {
			frameCountRef.current++;
			// Reveal four characters every frame (240 chars/second at 60fps)
			setVisibleChars(v => Math.min(v + 4, textContentRef.current.length));
			if (frameCountRef.current * 4 < textContentRef.current.length) {
				animationFrame = requestAnimationFrame(animate);
			}
		};
		
		animationFrame = requestAnimationFrame(animate);
		return () => cancelAnimationFrame(animationFrame);
	}, [visibleChars]);

	// Show button only after all text is visible
	const allTextVisible = visibleChars >= textContentRef.current.length;

	// Render the visible portion as HTML - simplified approach
	function renderTypewriter() {
		if (visibleChars >= textContentRef.current.length) {
			return fullTextRef.current; // Show full HTML when complete
		}
		
		// Count actual text characters to determine what to show
		let charCount = 0;
		let result = '';
		
		// Story 1 (highlighted)
		const story1 = t('storyBox.story1');
		if (charCount + story1.length <= visibleChars) {
			result += `<span class="storybox-highlight">${story1}</span><br/><br/>`;
			charCount += story1.length + 1; // +1 for space separator
		} else if (charCount < visibleChars) {
			result += `<span class="storybox-highlight">${story1.slice(0, visibleChars - charCount)}</span>`;
			return result;
		} else {
			return result;
		}
		
		// Story 2
		const story2 = t('storyBox.story2');
		if (charCount + story2.length <= visibleChars) {
			result += `${story2}<br/><br/>`;
			charCount += story2.length + 1;
		} else if (charCount < visibleChars) {
			result += story2.slice(0, visibleChars - charCount);
			return result;
		} else {
			return result;
		}
		
		// Story 3
		const story3 = t('storyBox.story3');
		if (charCount + story3.length <= visibleChars) {
			result += `${story3}<br/><br/>`;
			charCount += story3.length + 1;
		} else if (charCount < visibleChars) {
			result += story3.slice(0, visibleChars - charCount);
			return result;
		} else {
			return result;
		}
		
		// Story 4
		const story4 = t('storyBox.story4');
		if (charCount + story4.length <= visibleChars) {
			result += `${story4}<br/><br/>`;
			charCount += story4.length + 1;
		} else if (charCount < visibleChars) {
			result += story4.slice(0, visibleChars - charCount);
			return result;
		} else {
			return result;
		}
		
		// Story 5 (highlighted)
		const story5 = t('storyBox.story5');
		if (charCount < visibleChars) {
			result += `<span class="storybox-highlight">${story5.slice(0, visibleChars - charCount)}</span>`;
		}
		
		return result;
	}

	return (
		<div className="storybox-overlay">
			<div className="storybox-inner">
				<a
					href="https://www.pldspace.com/"
					target="_blank"
					rel="noopener noreferrer"
					className="storybox-logo-mobile-link"
					onClick={() => track('PLD_LOGO_CLICKED_MOBILE')}
				>
					<img
						src="/images/PLDSpace_Logo_White.svg"
						alt="DSL Logo"
						className="storybox-logo-mobile"
					/>
				</a>
				{/* Optionally, you can conditionally render the heading for desktop only if needed */}
				<div className="storybox-heading">{t('storyBox.heading')}</div>
				<div
					className="storybox-typewriter"
					dangerouslySetInnerHTML={{ __html: renderTypewriter() }}
				/>
				<div className="storybox-btn-row">
					<button
						className={`ui-congrats-btn storybox-fade-btn ${allTextVisible ? 'visible' : ''}`}
						onClick={() => { onContinue(); track('CAKE_TIME'); }}
					>
						{t('storyBox.continueButton')}
					</button>
				</div>
			</div>
		</div>
	);
}
